import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNotifications, Notification } from '../../../hooks/useNotifications';
import { supabase } from '../../../lib/supabase';

function timeAgo(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'たった今';
  if (mins < 60) return `${mins}分前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}時間前`;
  return `${Math.floor(hours / 24)}日前`;
}

function NotifTypeIcon({ type }: { type: Notification['type'] }) {
  if (type === 'diary_like' || type === 'material_like') {
    return (
      <div className="w-8 h-8 flex items-center justify-center bg-red-100 dark:bg-red-900/30 rounded-full flex-shrink-0">
        <i className="ri-heart-fill text-red-500 text-sm"></i>
      </div>
    );
  }
  if (type === 'achievement_like') {
    return (
      <div className="w-8 h-8 flex items-center justify-center bg-rose-100 dark:bg-rose-900/30 rounded-full flex-shrink-0">
        <i className="ri-heart-fill text-rose-500 text-sm"></i>
      </div>
    );
  }
  if (type === 'group_goal_achieved') {
    return (
      <div className="w-8 h-8 flex items-center justify-center bg-amber-100 dark:bg-amber-900/30 rounded-full flex-shrink-0">
        <i className="ri-trophy-fill text-amber-500 text-sm"></i>
      </div>
    );
  }
  if (type === 'achievement_posted') {
    return (
      <div className="w-8 h-8 flex items-center justify-center bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex-shrink-0">
        <i className="ri-trophy-fill text-emerald-500 text-sm"></i>
      </div>
    );
  }
  if (type === 'chat_message') {
    return (
      <div className="w-8 h-8 flex items-center justify-center bg-teal-100 dark:bg-teal-900/30 rounded-full flex-shrink-0">
        <i className="ri-chat-3-fill text-teal-500 text-sm"></i>
      </div>
    );
  }
  return (
    <div className="w-8 h-8 flex items-center justify-center bg-teal-100 dark:bg-teal-900/30 rounded-full flex-shrink-0">
      <i className="ri-chat-3-line text-teal-500 text-sm"></i>
    </div>
  );
}

export default function NotificationTab() {
  const navigate = useNavigate();
  const { notifications, unreadCount, loading, markAllAsRead, markAsRead } = useNotifications();

  // ページを開いたら自動で全既読処理
  useEffect(() => {
    if (!loading && unreadCount > 0) {
      markAllAsRead();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const likeNotifs = notifications.filter(n => n.type === 'diary_like' || n.type === 'material_like');
  const goalNotifs = notifications.filter(n => n.type === 'group_goal_achieved');
  const achievementLikeNotifs = notifications.filter(n => n.type === 'achievement_like');
  const achievementPostedNotifs = notifications.filter(n => n.type === 'achievement_posted');
  const chatNotifs = notifications.filter(n => n.type === 'chat_message');

  // チャット通知をグループ別に集計
  const chatByGroup: Record<string, { groupName: string; groupId: string; count: number; latestAt: string; actors: string[] }> = {};
  chatNotifs.forEach(n => {
    const key = n.target_id;
    if (!chatByGroup[key]) {
      chatByGroup[key] = { groupName: n.target_title || 'グループ', groupId: n.target_id, count: 0, latestAt: n.created_at, actors: [] };
    }
    chatByGroup[key].count++;
    if (n.actor_username && !chatByGroup[key].actors.includes(n.actor_username)) {
      chatByGroup[key].actors.push(n.actor_username);
    }
    if (n.created_at > chatByGroup[key].latestAt) chatByGroup[key].latestAt = n.created_at;
  });

  // いいね集計（同じ投稿へのいいね数）
  const likesByTarget: Record<string, { title: string; type: string; targetId: string; count: number; actors: string[] }> = {};
  likeNotifs.forEach(n => {
    const key = n.target_id;
    if (!likesByTarget[key]) {
      likesByTarget[key] = { title: n.target_title || '不明', type: n.target_type, targetId: n.target_id, count: 0, actors: [] };
    }
    likesByTarget[key].count++;
    if (n.actor_username && !likesByTarget[key].actors.includes(n.actor_username)) {
      likesByTarget[key].actors.push(n.actor_username);
    }
  });

  // 達成報告のgroup_idをSupabaseから取得してグループページへ遷移
  const navigateToAchievement = useCallback(async (achievementId: string, notifId: string) => {
    markAsRead(notifId);
    try {
      const { data } = await supabase
        .from('achievements')
        .select('group_id')
        .eq('id', achievementId)
        .maybeSingle();
      if (data?.group_id) {
        navigate(`/groups?groupId=${data.group_id}&section=achievements`);
      } else {
        navigate('/groups');
      }
    } catch {
      navigate('/groups');
    }
  }, [markAsRead, navigate]);

  const handleClick = useCallback(async (notif: Notification) => {
    markAsRead(notif.id);

    switch (notif.type) {
      case 'diary_like':
      case 'diary_comment':
        // 日記詳細ページへ直接遷移
        navigate(`/diary/${notif.target_id}`);
        break;

      case 'material_like':
      case 'material_comment':
        // 資料ページへ遷移（該当資料をハイライト）
        navigate(`/materials?highlight=${notif.target_id}`);
        break;

      case 'achievement_like':
        // 達成報告のgroup_idを取得してグループページへ
        await navigateToAchievement(notif.target_id, notif.id);
        break;

      case 'achievement_posted':
        // target_idはachievement_id → group_idを取得
        await navigateToAchievement(notif.target_id, notif.id);
        break;

      case 'chat_message':
        // グループページへ遷移してカレンダー（チャット）を自動オープン
        navigate(`/groups?groupId=${notif.target_id}&openCalendar=true`);
        break;

      case 'group_goal_achieved':
        navigate(`/groups?groupId=${notif.target_id}`);
        break;

      default:
        navigate('/groups');
    }
  }, [markAsRead, navigate, navigateToAchievement]);

  const getLabel = (type: Notification['type']) => {
    switch (type) {
      case 'diary_like': return 'があなたの日記にいいねしました';
      case 'material_like': return 'があなたの資料にいいねしました';
      case 'achievement_like': return 'があなたの達成報告にいいねしました';
      case 'achievement_posted': return 'が達成報告を投稿しました';
      case 'diary_comment': return 'があなたの日記にコメントしました';
      case 'material_comment': return 'があなたの資料にコメントしました';
      case 'group_goal_achieved': return 'がグループの週間目標を達成しました！';
      case 'chat_message': return 'がメッセージを送信しました';
      default: return '';
    }
  };

  const getDestinationLabel = (type: Notification['type']) => {
    switch (type) {
      case 'diary_like':
      case 'diary_comment': return '日記を見る →';
      case 'material_like':
      case 'material_comment': return '資料を見る →';
      case 'achievement_like':
      case 'achievement_posted': return 'グループを見る →';
      case 'chat_message': return 'チャットを開く →';
      case 'group_goal_achieved': return 'グループを見る →';
      default: return '確認する →';
    }
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6 sm:p-8">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
          <i className="ri-notification-3-line text-teal-500"></i>
          通知
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 bg-red-500 text-white text-xs font-bold rounded-full">{unreadCount}</span>
          )}
        </h2>
        <p className="text-xs text-gray-400 dark:text-gray-500">ページを開くと自動で既読になります</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : notifications.length === 0 ? (
        <div className="text-center py-16">
          <div className="w-16 h-16 flex items-center justify-center bg-gray-100 dark:bg-gray-700 rounded-full mx-auto mb-4">
            <i className="ri-notification-off-line text-3xl text-gray-400"></i>
          </div>
          <p className="text-gray-500 dark:text-gray-400 font-medium">通知はありません</p>
          <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">日記や資料にいいねされると通知が届きます</p>
        </div>
      ) : (
        <div className="space-y-6">

          {/* チャット未読通知 */}
          {Object.keys(chatByGroup).length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <i className="ri-chat-3-line text-teal-400"></i>
                グループチャット
              </h3>
              <div className="space-y-2">
                {Object.values(chatByGroup).map(item => (
                  <button
                    key={item.groupId}
                    onClick={() => navigate(`/groups?groupId=${item.groupId}&openCalendar=true`)}
                    className="w-full flex items-center gap-3 p-3 bg-teal-50 dark:bg-teal-900/10 border border-teal-100 dark:border-teal-900/20 rounded-xl hover:bg-teal-100 dark:hover:bg-teal-900/20 transition-colors cursor-pointer text-left"
                  >
                    <div className="w-8 h-8 flex items-center justify-center bg-teal-100 dark:bg-teal-900/30 rounded-full flex-shrink-0">
                      <i className="ri-chat-3-fill text-teal-500 text-sm"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 dark:text-gray-200 truncate">
                        {item.groupName}に未読メッセージがあります
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {item.actors.slice(0, 2).join('、')}{item.actors.length > 2 ? `ほか${item.actors.length - 2}人` : ''}からのメッセージ
                      </p>
                      <p className="text-xs text-teal-500 dark:text-teal-400 mt-0.5 font-medium">チャットを開く →</p>
                    </div>
                    <div className="flex flex-col items-end gap-1 flex-shrink-0">
                      <span className="px-2.5 py-1 bg-teal-500 text-white text-xs font-bold rounded-full">
                        {item.count}件
                      </span>
                      <span className="text-xs text-gray-400">{timeAgo(item.latestAt)}</span>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* グループ目標達成通知 */}
          {goalNotifs.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <i className="ri-trophy-line text-amber-400"></i>
                グループ目標達成
              </h3>
              <div className="space-y-2">
                {goalNotifs.map(n => (
                  <button
                    key={n.id}
                    onClick={() => handleClick(n)}
                    className="w-full flex items-start gap-3 p-3 rounded-xl transition-colors text-left cursor-pointer hover:bg-amber-50 dark:hover:bg-amber-900/10"
                  >
                    <div className="w-8 h-8 flex items-center justify-center bg-amber-100 dark:bg-amber-900/30 rounded-full flex-shrink-0">
                      <i className="ri-trophy-fill text-amber-500 text-sm"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                        <span className="text-amber-600 dark:text-amber-400">{n.actor_username}</span>
                        <span className="text-gray-600 dark:text-gray-400 font-normal">がグループの週間目標を達成しました！</span>
                      </p>
                      {n.target_title && (
                        <p className="text-xs text-amber-600 dark:text-amber-400 mt-0.5 truncate font-medium">{n.target_title}</p>
                      )}
                      <p className="text-xs text-gray-400 mt-0.5">{timeAgo(n.created_at)}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 達成報告投稿通知 */}
          {achievementPostedNotifs.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <i className="ri-trophy-line text-emerald-400"></i>
                グループの達成報告
              </h3>
              <div className="space-y-2">
                {achievementPostedNotifs.map(n => (
                  <button
                    key={n.id}
                    onClick={() => handleClick(n)}
                    className="w-full flex items-start gap-3 p-3 rounded-xl transition-colors text-left cursor-pointer hover:bg-emerald-50 dark:hover:bg-emerald-900/10"
                  >
                    <div className="w-8 h-8 flex items-center justify-center bg-emerald-100 dark:bg-emerald-900/30 rounded-full flex-shrink-0">
                      <i className="ri-trophy-fill text-emerald-500 text-sm"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 dark:text-gray-200">
                        <span className="font-semibold">{n.actor_username}</span>
                        <span className="text-gray-600 dark:text-gray-400">が達成報告を投稿しました</span>
                      </p>
                      {n.target_title && (
                        <p className="text-xs text-emerald-600 dark:text-emerald-400 mt-0.5 truncate font-medium">「{n.target_title}」</p>
                      )}
                      <p className="text-xs text-emerald-500 dark:text-emerald-400 mt-0.5 font-medium">グループを見る →</p>
                      <p className="text-xs text-gray-400 mt-0.5">{timeAgo(n.created_at)}</p>
                    </div>
                    {!n.is_read && <div className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0 mt-2"></div>}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 達成報告いいね通知 */}
          {achievementLikeNotifs.length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <i className="ri-heart-line text-rose-400"></i>
                達成報告へのいいね
              </h3>
              <div className="space-y-2">
                {achievementLikeNotifs.map(n => (
                  <button
                    key={n.id}
                    onClick={() => handleClick(n)}
                    className="w-full flex items-start gap-3 p-3 rounded-xl transition-colors text-left cursor-pointer hover:bg-rose-50 dark:hover:bg-rose-900/10"
                  >
                    <div className="w-8 h-8 flex items-center justify-center bg-rose-100 dark:bg-rose-900/30 rounded-full flex-shrink-0">
                      <i className="ri-heart-fill text-rose-500 text-sm"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 dark:text-gray-200">
                        <span className="font-semibold">{n.actor_username}</span>
                        <span className="text-gray-600 dark:text-gray-400">があなたの達成報告にいいねしました</span>
                      </p>
                      {n.target_title && (
                        <p className="text-xs text-rose-600 dark:text-rose-400 mt-0.5 truncate font-medium">「{n.target_title}」</p>
                      )}
                      <p className="text-xs text-rose-500 dark:text-rose-400 mt-0.5 font-medium">達成報告を見る →</p>
                      <p className="text-xs text-gray-400 mt-0.5">{timeAgo(n.created_at)}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* いいね集計サマリー */}
          {Object.keys(likesByTarget).length > 0 && (
            <div>
              <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
                <i className="ri-heart-line text-red-400"></i>
                いいね集計
              </h3>
              <div className="space-y-2">
                {Object.values(likesByTarget).map(item => (
                  <button
                    key={item.targetId}
                    onClick={() => {
                      if (item.type === 'diary') navigate(`/diary/${item.targetId}`);
                      else navigate(`/materials?highlight=${item.targetId}`);
                    }}
                    className="w-full flex items-center gap-3 p-3 bg-red-50 dark:bg-red-900/10 border border-red-100 dark:border-red-900/20 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/20 transition-colors cursor-pointer text-left"
                  >
                    <div className="w-8 h-8 flex items-center justify-center bg-red-100 dark:bg-red-900/30 rounded-full flex-shrink-0">
                      <i className="ri-heart-fill text-red-500 text-sm"></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-800 dark:text-gray-200 truncate">「{item.title}」</p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                        {item.actors.slice(0, 3).join('、')}{item.actors.length > 3 ? `ほか${item.actors.length - 3}人` : ''}
                      </p>
                      <p className="text-xs text-red-500 dark:text-red-400 mt-0.5 font-medium">
                        {item.type === 'diary' ? '日記を見る →' : '資料を見る →'}
                      </p>
                    </div>
                    <span className="flex-shrink-0 px-2.5 py-1 bg-red-500 text-white text-xs font-bold rounded-full">
                      {item.count}件
                    </span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* 全通知タイムライン */}
          <div>
            <h3 className="text-sm font-bold text-gray-700 dark:text-gray-300 mb-3 flex items-center gap-2">
              <i className="ri-time-line text-gray-400"></i>
              タイムライン
            </h3>
            <div className="space-y-1">
              {notifications.map(notif => {
                const label = getLabel(notif.type);
                const destLabel = getDestinationLabel(notif.type);
                return (
                  <button
                    key={notif.id}
                    onClick={() => handleClick(notif)}
                    className={`w-full flex items-start gap-3 p-3 rounded-xl transition-colors text-left cursor-pointer ${
                      !notif.is_read
                        ? 'bg-teal-50 dark:bg-teal-900/10 hover:bg-teal-100 dark:hover:bg-teal-900/20'
                        : 'hover:bg-gray-50 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <div className="relative flex-shrink-0">
                      {notif.actor_avatar_url ? (
                        <img src={notif.actor_avatar_url} alt={notif.actor_username} className="w-9 h-9 rounded-full object-cover" />
                      ) : (
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-teal-400 to-emerald-400 flex items-center justify-center text-white text-sm font-semibold">
                          {(notif.actor_username || '?').charAt(0).toUpperCase()}
                        </div>
                      )}
                      <div className="absolute -bottom-0.5 -right-0.5 w-4 h-4 flex items-center justify-center bg-white dark:bg-gray-800 rounded-full border border-gray-200 dark:border-gray-700">
                        <NotifTypeIcon type={notif.type} />
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-gray-800 dark:text-gray-200 leading-snug">
                        <span className="font-semibold">{notif.actor_username}</span>
                        <span className="text-gray-600 dark:text-gray-400">{label}</span>
                      </p>
                      {notif.target_title && (
                        <p className={`text-xs mt-0.5 truncate font-medium ${
                          notif.type === 'group_goal_achieved' ? 'text-amber-600 dark:text-amber-400' :
                          notif.type === 'achievement_posted' || notif.type === 'achievement_like' ? 'text-emerald-600 dark:text-emerald-400' :
                          notif.type === 'chat_message' ? 'text-teal-600 dark:text-teal-400' :
                          'text-teal-600 dark:text-teal-400'
                        }`}>
                          {notif.type === 'group_goal_achieved' || notif.type === 'chat_message' ? notif.target_title : `「${notif.target_title}」`}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-0.5">{timeAgo(notif.created_at)}</p>
                      <p className="text-xs text-teal-500 dark:text-teal-400 mt-0.5 font-medium">{destLabel}</p>
                    </div>
                    {!notif.is_read && <div className="w-2 h-2 rounded-full bg-teal-500 flex-shrink-0 mt-2"></div>}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
